/**
 * Size guide popup.
 *
 * Extracted verbatim from snippets/size-guide-popup.liquid, where it was an
 * inline <script>. Inline, this ~45 KB of JavaScript was re-sent uncached in
 * the HTML of every single product page and had to be parsed during the
 * document parse. As an asset it is fetched once and then served from cache
 * for the rest of the session, and it no longer inflates the PDP document.
 *
 * The snippet still emits its own <script type="application/json"
 * id="<sg_id>-data"> block - all Liquid-derived configuration is read from
 * there at open() time, so this file is pure static JavaScript.
 *
 * Loading: <script defer>. Safe because the only entry point is a delegated
 * click listener on document (see the bottom of this file), so nothing here
 * depends on running at a particular point in the parse.
 */
(function () {
  /* Idempotency guard: product-info-copy can be rendered more than once per
     document (main product plus the quick-buy template), and defer-loaded
     scripts execute once per tag. Registering the delegated listener twice
     would open the dialog twice on a single click. */
  if (window.__sgPopupScriptInit) return;
  window.__sgPopupScriptInit = true;

(function () {
  'use strict';

  /* ── Canvas helpers — mirror the AR live-mockup's frame renderer ─────────── */
  var SG_DPR = Math.min(window.devicePixelRatio || 1, 2);
  var _SG_TEX_CACHE = {};
  var _SG_STRIP_CACHE = new Map();

  function sgLoadTexture(url) {
    if (!url) return Promise.resolve(null);
    if (_SG_TEX_CACHE[url]) return _SG_TEX_CACHE[url];
    _SG_TEX_CACHE[url] = new Promise(function (resolve) {
      var img = new Image();
      img.onload  = function () { resolve(img); };
      img.onerror = function () { resolve(null); };
      img.src = url;
    });
    return _SG_TEX_CACHE[url];
  }

  /* Pre-render a frame strip to an offscreen canvas. Texture is stretched
     (never tiled) over the strip length, rotated 90° for vertical pieces
     so the grain runs along the frame, plus a symmetric cross-profile
     gradient to fake the moulding bevel. Cached by identity + dims.

     frameLabel is the lowercase frame-option name (e.g. "natural oak",
     "white", "black"). Pale frames get a softer gradient so the bevel
     doesn't crush the wood grain into a dark band. */
  function sgIsLightFrame(label) {
    if (!label) return false;
    var s = String(label).toLowerCase();
    return /natural|oak|white|gold/.test(s);
  }
  function sgMakeFrameStrip(tex, stripLength, stripThickness, vertical, frameLabel) {
    var L = Math.max(1, Math.round(stripLength));
    var T = Math.max(1, Math.round(stripThickness));
    var isLight = sgIsLightFrame(frameLabel);
    var key = (tex && tex.src ? tex.src : '?') + '|' + L + 'x' + T + '|' +
              (vertical ? 'v' : 'h') + '|' + (isLight ? 'L' : 'D');
    var cached = _SG_STRIP_CACHE.get(key);
    if (cached) return cached;

    var off = document.createElement('canvas');
    var w = vertical ? T : L;
    var h = vertical ? L : T;
    off.width = w; off.height = h;
    var ctx = off.getContext('2d');
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    if (vertical) {
      ctx.save();
      ctx.translate(T, 0);
      ctx.rotate(Math.PI / 2);
      ctx.drawImage(tex, 0, 0, L, T);
      ctx.restore();
    } else {
      ctx.drawImage(tex, 0, 0, L, T);
    }

    var edge = isLight ? 0.07 : 0.20;
    var mid  = isLight ? 0.015 : 0.04;
    var hi   = isLight ? 0.06 : 0.10;
    var grad = vertical
      ? ctx.createLinearGradient(0, 0, w, 0)
      : ctx.createLinearGradient(0, 0, 0, h);
    grad.addColorStop(0.00, 'rgba(0,0,0,' + edge + ')');
    grad.addColorStop(0.14, 'rgba(0,0,0,' + mid  + ')');
    grad.addColorStop(0.44, 'rgba(255,255,255,' + hi + ')');
    grad.addColorStop(0.56, 'rgba(255,255,255,' + hi + ')');
    grad.addColorStop(0.86, 'rgba(0,0,0,' + mid  + ')');
    grad.addColorStop(1.00, 'rgba(0,0,0,' + edge + ')');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, w, h);

    if (_SG_STRIP_CACHE.size > 48) {
      var firstKey = _SG_STRIP_CACHE.keys().next().value;
      _SG_STRIP_CACHE.delete(firstKey);
    }
    _SG_STRIP_CACHE.set(key, off);
    return off;
  }

  /* Paint a full framed piece (reveal + mitred frame) onto the given canvas.
     Canvas is sized to outerW × outerH (CSS px) with DPR scaling. */
  function sgPaintFrame(canvas, tex, outerW, outerH, framePx, revealPx, frameLabel) {
    var cssW = Math.max(1, Math.round(outerW));
    var cssH = Math.max(1, Math.round(outerH));
    canvas.width  = Math.max(1, Math.round(cssW * SG_DPR));
    canvas.height = Math.max(1, Math.round(cssH * SG_DPR));
    canvas.style.width  = '100%';
    canvas.style.height = '100%';
    var ctx = canvas.getContext('2d');
    ctx.setTransform(SG_DPR, 0, 0, SG_DPR, 0, 0);
    ctx.clearRect(0, 0, cssW, cssH);

    // 1) Dark reveal fills the whole area; frame paints over the outer band.
    ctx.fillStyle = '#0a0a0a';
    ctx.fillRect(0, 0, cssW, cssH);

    var t = framePx;
    var strips = [
      { clip: [[0,0],[cssW,0],[cssW - t,t],[t,t]],
        rect: [0, 0, cssW, t],
        strip: sgMakeFrameStrip(tex, cssW, t, false, frameLabel) },
      { clip: [[0,cssH],[t,cssH - t],[cssW - t,cssH - t],[cssW,cssH]],
        rect: [0, cssH - t, cssW, t],
        strip: sgMakeFrameStrip(tex, cssW, t, false, frameLabel) },
      { clip: [[0,0],[t,t],[t,cssH - t],[0,cssH]],
        rect: [0, 0, t, cssH],
        strip: sgMakeFrameStrip(tex, cssH, t, true, frameLabel) },
      { clip: [[cssW,0],[cssW,cssH],[cssW - t,cssH - t],[cssW - t,t]],
        rect: [cssW - t, 0, t, cssH],
        strip: sgMakeFrameStrip(tex, cssH, t, true, frameLabel) }
    ];
    strips.forEach(function (s) {
      ctx.save();
      ctx.beginPath();
      s.clip.forEach(function (p, i) {
        if (i === 0) ctx.moveTo(p[0], p[1]);
        else         ctx.lineTo(p[0], p[1]);
      });
      ctx.closePath();
      ctx.clip();
      ctx.drawImage(s.strip, s.rect[0], s.rect[1], s.rect[2], s.rect[3]);
      ctx.restore();
    });

    // Mitre seams — the diagonal hairlines where two frame pieces meet
    // at each corner. Kept faint and thin so they read as seams rather
    // than drawn-on lines.
    ctx.save();
    ctx.strokeStyle = 'rgba(0,0,0,0.18)';
    ctx.lineWidth = 0.4;
    ctx.beginPath();
    ctx.moveTo(0, 0);          ctx.lineTo(t, t);
    ctx.moveTo(cssW, 0);       ctx.lineTo(cssW - t, t);
    ctx.moveTo(0, cssH);       ctx.lineTo(t, cssH - t);
    ctx.moveTo(cssW, cssH);    ctx.lineTo(cssW - t, cssH - t);
    ctx.stroke();
    ctx.restore();

    // Outer bevel highlight
    ctx.save();
    ctx.strokeStyle = 'rgba(255,255,255,0.08)';
    ctx.lineWidth = 0.5;
    ctx.strokeRect(0.5, 0.5, cssW - 1, cssH - 1);
    ctx.restore();

    // Inner highlight where moulding meets reveal
    ctx.save();
    ctx.strokeStyle = 'rgba(255,255,255,0.12)';
    ctx.lineWidth = 1;
    ctx.strokeRect(t + 0.5, t + 0.5, cssW - 2 * t - 1, cssH - 2 * t - 1);
    ctx.restore();
  }

  /* ── Size notes ──────────────────────────────────────────────────────────── */
  const SIZE_NOTES = {
    '12x18':  'An intimate scale (≈30×45 cm). Works beautifully in pairs or as part of a gallery wall — never overwhelming.',
    '16x24':  'A versatile mid-size at roughly 40×60 cm. Suits bedrooms, home offices and hallways without overpowering the room.',
    '24x36':  'The most popular 2:3 format at roughly 60×90 cm. Strong enough to stand alone above a console or sofa.',
    '32x48':  'A commanding 80×120 cm piece. Walls wider than 6 ft let this size truly breathe.',
    '40x60':  'Gallery scale at 100×150 cm. This size defines the room it inhabits.',
    '12x16':  'A neat 30×40 cm format — perfect for shelves, desks or intimate gallery wall clusters.',
    '18x24':  'A clean 45×60 cm mid-size. Works quietly in pairs or as an accent above smaller furniture.',
    '24x32':  'Balanced and versatile at 60×80 cm — a natural fit for bedrooms and dining spaces.',
    '30x40':  'The sweet spot at 76×100 cm. Anchors a room gracefully above a sideboard, console or mantel.',
    '36x48':  'Statement scale at 90×120 cm — best for generous walls in living rooms or entrance halls.',
    '12x12':  'A compact 30×30 cm square that thrives in multiples — arrange three in a row for real impact.',
    '16x16':  'An easy 40×40 cm accent. Sits naturally on a shelf or anchors a small wall grouping.',
    '20x20':  'A confident 50×50 cm square that holds its own as a solo piece in smaller rooms.',
    '24x24':  'The most popular square at 60×60 cm. Centres beautifully above a console or fireplace.',
    '30x30':  'A striking 76×76 cm square — exceptional above a fireplace or as a dining room centrepiece.',
    '36x36':  'Gallery scale at 90×90 cm. One piece fills a wall with quiet, confident authority.',
    '40x40':  'A bold 100×100 cm square. Reserve this for your most prominent wall.',
    '12x24':  'A subtle panoramic at 30×60 cm — ideal above console tables on narrow horizontal walls.',
    '16x32':  'A confident 40×80 cm wide-format — works above beds and low sideboards.',
    '24x48':  'A wide 60×120 cm statement that anchors sofas, beds and open-plan dining areas.',
    '30x60':  'A bold 76×150 cm panoramic. Works best on walls 8 ft or wider.',
    '37x74':  'Extra-large at roughly 94×188 cm — a room-defining panoramic for your grandest wall.',
    '8x24':   'A slim 20×60 cm accent — ideal for narrow walls, beside doors or in tight corridors.',
    '12x36':  'An elegant 30×90 cm piece that elongates a space. Perfect for hallways and beside windows.',
    '16x48':  'A tall 40×120 cm piece that draws the eye upward. Works beautifully in entryways and stairwells.',
    '20x60':  'The most popular tall format at 50×150 cm. Anchors hallways and acts as a bold accent beside a sofa.',
    '24x72':  'A striking 60×183 cm piece — six feet of presence for high-ceilinged rooms and grand entryways.',
    '8x32':   'A slim, architectural 20×80 cm piece — ideal as a flanking accent beside a mirror or fireplace.',
    '12x48':  'A dramatic 30×120 cm vertical tailored for stairwells and walls that need height.',
    '15x60':  'A commanding 38×150 cm piece — makes a bold statement in entryways with generous ceiling heights.',
    '20x80':  'Gallery height at 50×200 cm. Nearly seven feet tall — reserved for truly exceptional spaces.',
    '30x40cm':   'A compact classic at 30×40 cm. Works in intimate spaces, home offices and gallery wall arrangements.',
    '40x50cm':   'A confident mid-size at 40×50 cm. Well-suited to bedrooms, dining rooms and smaller living areas.',
    '50x70cm':   'The most versatile poster format at 50×70 cm. Confident above a desk, beside a window or in a curated gallery wall.',
    '70x100cm':  'A bold 70×100 cm statement. Works above a sofa or as a standalone on a large feature wall.',
    '100x140cm': 'Gallery scale at 100×140 cm. This size commands the room it lives in.',
    '50x50cm':   'A balanced 50×50 cm square — confident alone or paired with another piece.',
    '70x70cm':   'A strong 70×70 cm square. Exceptional above a fireplace, console or as a solo dining room piece.',
    '100x100cm': 'A bold, gallery-scale 100×100 cm square — for your most prominent wall.',
    'a4':        'A compact A4 (21×30 cm) — ideal for shelf framing, desktop displays or gallery wall clusters.',
    'a5':        'The smallest format at A5 (15×21 cm) — perfect for shelf displays and intimate gallery groupings.',
  };

  /* ── Room placement tips ─────────────────────────────────────────────────── */
  const ROOM_TIPS = {
    room1: 'For a 7–8 ft sofa, artwork covering ⅔ of the sofa width — typically 40–60" — creates a balanced look. Hang the bottom of the frame 6–8" above the sofa back.',
    room2: 'Artwork matching your mantel width (usually 36–48") creates a strong focal point. Centre it precisely above the fireplace opening.',
    room3: 'Above a buffet or sideboard, a 24–36" wide piece anchors the dining room. Leave 6–8" between the top of the furniture and the bottom of the frame.',
    room4: 'Choose artwork ⅔ the width of your headboard and hang so the bottom sits 6" above your pillow tops — deliberate and serene.',
    room5: 'Vertical pieces are ideal for hallways — they draw the eye up and make the space feel taller.',
  };

  /* ── Popular size derived from product ratio + orientation ───────────────── */
  const POPULAR_MAP = {
    '3x1_landscape': '20×60"', '3x1_portrait':  '16×48"',
    '3x4_portrait':  '30×40"', '3x4_landscape': '36×48"',
    '2x3_portrait':  '24×36"', '2x3_landscape': '32×48"',
    '2x1_landscape': '30×60"', '1x1':           '30×30"',
    '1x4_portrait':  '15×60"',
  };

  function detectRatioType(variants, sizeOptionIndex) {
    const optKey = 'option' + (sizeOptionIndex + 1);
    for (const v of variants) {
      const val = v[optKey] || '';
      // Prefer inch part in combined strings
      const hasInch = val.includes('"') || val.includes('\u201d');
      const match = hasInch
        ? val.match(/(\d+)\s*[x\u00d7]\s*(\d+)/i)
        : (val.match(/(\d+)\s*[x\u00d7]\s*(\d+)\s*cm/i) || val.match(/(\d+)\s*[x\u00d7]\s*(\d+)/i));
      if (!match) continue;
      const [a, b] = [+match[1], +match[2]];
      const r = Math.max(a,b) / Math.min(a,b);
      if (Math.abs(r - 1)     < 0.06)  return '1x1';
      if (Math.abs(r - 1.333) < 0.12)  return '3x4';
      if (Math.abs(r - 1.5)   < 0.12)  return '2x3';
      if (Math.abs(r - 2)     < 0.18)  return '2x1';
      if (Math.abs(r - 3)     < 0.25)  return '3x1';
      if (Math.abs(r - 4)     < 0.35)  return '1x4';
      break;
    }
    return null;
  }

  function getPopularForProduct(ratioType, orientation, variants, sizeOptionIndex) {
    if (!ratioType) return null;
    const key = ratioType === '1x1' ? '1x1' : ratioType + '_' + orientation;
    const candidate = POPULAR_MAP[key];
    if (!candidate) return null;
    // Verify this size actually exists in the product's variants
    const optKey = 'option' + (sizeOptionIndex + 1);
    const cNorm  = candidate.replace(/[×\u201d"]/g, 'x').replace(/[^0-9x]/gi, '').toLowerCase();
    const exists = variants.some(v => {
      const raw = v[optKey] || '';
      // Extract the inch part of combined strings
      const hasInch = raw.includes('"') || raw.includes('\u201d');
      const m = hasInch ? raw.match(/(\d+)\s*[x\u00d7]\s*(\d+)/i)
                        : raw.match(/(\d+)\s*[x\u00d7]\s*(\d+)/i);
      if (!m) return false;
      return (m[1] + 'x' + m[2]) === cNorm;
    });
    return exists ? candidate : null;
  }

  /* ── Parse size → physical { w, h } in inches ────────────────────────────── */
  /* Uses the artwork image aspect ratio (artW/artH) to determine orientation  */
  /* instead of relying on the first number in the size string.                */
  function parseSizeInches(str, orientation, artW, artH) {
    if (!str) return null;
    const lower = str.toLowerCase();

    /* Determine actual orientation from artwork image dimensions if available */
    let orient = orientation;
    if (artW > 0 && artH > 0) {
      if (artW > artH) orient = 'landscape';
      else if (artW === artH) orient = 'square';
      else orient = 'portrait';
    }

    if (lower.includes('a4')) return orient === 'landscape' ? { w: 11.7, h: 8.3 } : { w: 8.3, h: 11.7 };
    if (lower.includes('a5')) return orient === 'landscape' ? { w: 8.3, h: 5.8 }  : { w: 5.8, h: 8.3  };

    const hasInch = str.includes('"') || str.includes('\u201d');

    if (hasInch) {
      const m = str.match(/(\d+)\s*[x\u00d7]\s*(\d+)/i);
      if (m) {
        const [a, b] = [+m[1], +m[2]];
        const [lg, sm] = [Math.max(a,b), Math.min(a,b)];
        const [w, h] = orient === 'landscape' ? [lg, sm]
                     : orient === 'square'    ? [a,  b ]
                     :                          [sm, lg ];
        return { w, h };
      }
    }

    const cm = str.match(/(\d+)\s*[x\u00d7]\s*(\d+)\s*cm/i);
    if (cm) {
      const [a, b] = [+cm[1], +cm[2]];
      const [lg, sm] = [Math.max(a,b), Math.min(a,b)];
      const [wCm, hCm] = orient === 'landscape' ? [lg, sm]
                       : orient === 'square'    ? [a,  b ]
                       :                          [sm, lg ];
      return { w: +(wCm / 2.54).toFixed(1), h: +(hCm / 2.54).toFixed(1) };
    }

    const bare = str.match(/(\d+)\s*[x\u00d7]\s*(\d+)/i);
    if (bare) {
      const [a, b] = [+bare[1], +bare[2]];
      const [lg, sm] = [Math.max(a,b), Math.min(a,b)];
      const [w, h] = orient === 'landscape' ? [lg, sm]
                   : orient === 'square'    ? [a,  b ]
                   :                          [sm, lg ];
      return { w, h };
    }
    return null;
  }

  /* ── Format the dimension display from the raw option string ─────────────── */
  /* Uses artwork image dimensions to determine correct W×H order.             */
  function formatDimDisplay(str, orientation, artW, artH) {
    if (!str) return { primary: '—', secondary: '' };
    const lower = str.toLowerCase();

    /* Determine actual orientation from artwork dimensions if available */
    let orient = orientation || 'portrait';
    if (artW > 0 && artH > 0) {
      if (artW > artH) orient = 'landscape';
      else if (artW === artH) orient = 'square';
      else orient = 'portrait';
    }

    if (lower.includes('a4')) {
      return orient === 'landscape'
        ? { primary: 'A4', secondary: '30 × 21 cm' }
        : { primary: 'A4', secondary: '21 × 30 cm' };
    }
    if (lower.includes('a5')) {
      return orient === 'landscape'
        ? { primary: 'A5', secondary: '21 × 15 cm' }
        : { primary: 'A5', secondary: '15 × 21 cm' };
    }

    const hasInch  = str.includes('"') || str.includes('\u201d');
    const inchMatch = str.match(/(\d+)\s*[x\u00d7]\s*(\d+)/i);
    const cmMatch   = str.match(/(\d+)\s*[x\u00d7]\s*(\d+)\s*cm/i);

    if (hasInch && inchMatch) {
      const [a, b] = [+inchMatch[1], +inchMatch[2]];
      const [lg, sm] = [Math.max(a,b), Math.min(a,b)];
      let w, h;
      if (orient === 'landscape') { w = lg; h = sm; }
      else if (orient === 'square') { w = a; h = b; }
      else { w = sm; h = lg; }
      const primary = w + '\u00d7' + h + '"';
      const wCm = Math.round(w * 2.54), hCm = Math.round(h * 2.54);
      const secondary = cmMatch
        ? (orient === 'landscape' ? Math.max(+cmMatch[1],+cmMatch[2]) + ' \u00d7 ' + Math.min(+cmMatch[1],+cmMatch[2]) + ' cm'
          : orient === 'square' ? cmMatch[1] + ' \u00d7 ' + cmMatch[2] + ' cm'
          : Math.min(+cmMatch[1],+cmMatch[2]) + ' \u00d7 ' + Math.max(+cmMatch[1],+cmMatch[2]) + ' cm')
        : wCm + ' \u00d7 ' + hCm + ' cm';
      return { primary, secondary };
    }

    if (cmMatch) {
      const [a, b] = [+cmMatch[1], +cmMatch[2]];
      const [lg, sm] = [Math.max(a,b), Math.min(a,b)];
      let wCm, hCm;
      if (orient === 'landscape') { wCm = lg; hCm = sm; }
      else if (orient === 'square') { wCm = a; hCm = b; }
      else { wCm = sm; hCm = lg; }
      return {
        primary:   wCm + ' \u00d7 ' + hCm + ' cm',
        secondary: Math.round(wCm / 2.54) + '" \u00d7 ' + Math.round(hCm / 2.54) + '"',
      };
    }

    return { primary: str, secondary: '' };
  }

  /* ── Normalise size string → SIZE_NOTES key ─────────────────────────────── */
  function normSizeKey(str) {
    if (!str) return '';
    const lower = str.toLowerCase().trim();
    if (lower.includes('a4')) return 'a4';
    if (lower.includes('a5')) return 'a5';
    const hasInch = str.includes('"') || str.includes('\u201d');
    if (hasInch) {
      const m = str.match(/(\d+)\s*[x\u00d7]\s*(\d+)/i);
      if (m) return m[1] + 'x' + m[2];
    }
    const cm = lower.match(/(\d+)\s*[x\u00d7]\s*(\d+)\s*cm/);
    if (cm) return cm[1] + 'x' + cm[2] + 'cm';
    const bare = lower.match(/(\d+)\s*[x\u00d7]\s*(\d+)/);
    if (bare) return bare[1] + 'x' + bare[2];
    return lower;
  }

  /* ══ Main class ══════════════════════════════════════════════════════════════ */
  class SizeGuide {
    constructor(id) {
      this.id     = id;
      this.dialog = document.getElementById(id);
      if (!this.dialog) return;
      const raw = document.getElementById(id + '-data');
      if (!raw) return;
      this.cfg = JSON.parse(raw.textContent);

      this.orientation      = this.cfg.orientation || 'portrait';
      this.artUrl           = this.cfg.artworkUrl || '';
      this.artW             = this.cfg.artworkWidth || 0;
      this.artH             = this.cfg.artworkHeight || 0;
      // Optional secondary artwork crop (e.g. a 1×4 version alongside a
      // 1×3 primary). Used for sizes whose aspect ratio is closer to the
      // alt crop — matches the product-page live mockup's auto-pick.
      this.artAltUrl        = this.cfg.artworkAltUrl || '';
      this.artAltW          = this.cfg.artworkAltWidth || 0;
      this.artAltH          = this.cfg.artworkAltHeight || 0;
      this.productType      = this.cfg.productType || 'canvas';
      this.currentVariantId = this.cfg.selectedVariantId;
      this.currentRoomId    = null;
      this.frameOptionIndex = (typeof this.cfg.frameOptionIndex === 'number') ? this.cfg.frameOptionIndex : -1;
      this.frameValues      = Array.isArray(this.cfg.frameValues) ? this.cfg.frameValues : [];

      this.ratioType   = detectRatioType(this.cfg.variants, this.cfg.sizeOptionIndex);
      this.popularSize = getPopularForProduct(
        this.ratioType, this.orientation, this.cfg.variants, this.cfg.sizeOptionIndex
      );

      this._bind();
      this._buildPills();
      this._buildFramePills();
      this._activateRoom(this.dialog.querySelector('.sg-room')?.dataset.room);
    }

    open() {
      const sel = document.querySelector('[name="id"]');
      if (sel && sel.value) this.currentVariantId = +sel.value;
      this.dialog.showModal();
      this._preloadFrameTextures();
      this._preloadArtworkImages();
      this._highlightPills();
      this._highlightFramePills();
      this._refreshScene();
    }

    /* Kick off texture loads for every frame option the moment the popup
       opens. First click on a frame pill then shows the painted frame
       instantly instead of waiting for an image fetch. */
    _preloadFrameTextures() {
      if (this._texturesPreloaded) return;
      this._texturesPreloaded = true;
      if (!Array.isArray(this.frameValues)) return;
      this.frameValues.forEach(fv => { if (fv && fv.texture) sgLoadTexture(fv.texture); });
    }

    /* Preload both artwork crops so swapping between 1×3 and 1×4 sizes
       doesn't flash a blank frame while the alt image fetches. */
    _preloadArtworkImages() {
      if (this._artPreloaded) return;
      this._artPreloaded = true;
      [this.artUrl, this.artAltUrl].forEach(url => {
        if (url) { const i = new Image(); i.src = url; }
      });
    }

    /* Pick the artwork crop whose aspect ratio is closer (in log-space)
       to the selected size. Mirrors live-mockup.js's _pickArtwork logic
       so the popup matches what the product-page preview shows. */
    _pickArtworkUrl(size) {
      if (!this.artAltUrl) return this.artUrl;
      if (!this.artUrl)    return this.artAltUrl;
      if (!size || !size.w || !size.h) return this.artUrl;
      const pR = (this.artW    && this.artH)    ? this.artW    / this.artH    : null;
      const aR = (this.artAltW && this.artAltH) ? this.artAltW / this.artAltH : null;
      if (!pR) return this.artAltUrl;
      if (!aR) return this.artUrl;
      // Compare against BOTH orderings of the size (portrait/landscape
      // are mirror aspect ratios — 1:3 and 3:1 both "fit" a 1×3 crop).
      const t  = size.w / size.h;
      const ti = size.h / size.w;
      const diff = (r, tgt) => Math.abs(Math.log(r) - Math.log(tgt));
      const pBest = Math.min(diff(pR, t), diff(pR, ti));
      const aBest = Math.min(diff(aR, t), diff(aR, ti));
      return pBest <= aBest ? this.artUrl : this.artAltUrl;
    }

    close() { this.dialog.close(); }

    _bind() {
      this.dialog.querySelectorAll('[data-sg-close]')
        .forEach(b => b.addEventListener('click', () => this.close()));
      this.dialog.addEventListener('click', e => { if (e.target === this.dialog) this.close(); });
      this.dialog.querySelectorAll('.sg-room-tab').forEach(t =>
        t.addEventListener('click', () => this._activateRoom(t.dataset.target)));
      const atcBtn = this.dialog.querySelector('[data-sg-atc]');
      if (atcBtn) {
        atcBtn.addEventListener('click', () => {
          const v = this._currentVariant();
          if (!v || !v.available) return;
          this._addToCart(v, atcBtn);
        });
      }
    }

    _buildPills() {
      const pillsEl = this.dialog.querySelector('[data-sg-pills]');
      if (!pillsEl) return;
      const optKey = 'option' + (this.cfg.sizeOptionIndex + 1);
      const seen = new Set();
      const unique = this.cfg.variants.filter(v => {
        if (seen.has(v[optKey])) return false;
        seen.add(v[optKey]); return true;
      });
      pillsEl.innerHTML = '';
      unique.forEach(v => {
        const btn = document.createElement('button');
        btn.className        = 'sg-size-pill';
        btn.type             = 'button';
        btn.textContent      = v[optKey];
        btn.dataset.variantId = v.id;
        btn.dataset.sizeVal  = v[optKey];
        if (!v.available) btn.disabled = true;
        btn.addEventListener('click', () => {
          this._onSizeSelect(v[optKey]);
        });
        pillsEl.appendChild(btn);
      });
    }

    _buildFramePills() {
      const wrap   = this.dialog.querySelector('[data-sg-frames-wrap]');
      const pillsEl = this.dialog.querySelector('[data-sg-frame-pills]');
      if (!wrap || !pillsEl) return;
      if (this.frameOptionIndex < 0 || !this.frameValues.length) {
        wrap.hidden = true;
        return;
      }
      wrap.hidden = false;
      pillsEl.innerHTML = '';
      this.frameValues.forEach(fv => {
        const btn = document.createElement('button');
        btn.className = 'sg-frame-pill';
        btn.type = 'button';
        btn.dataset.frameVal = fv.name;
        const swatch = document.createElement('span');
        swatch.className = 'sg-frame-pill__swatch';
        if (fv.image) {
          swatch.style.backgroundImage = 'url(' + fv.image + ')';
        } else if (fv.color) {
          swatch.style.background = fv.color;
        }
        const label = document.createElement('span');
        label.className = 'sg-frame-pill__label';
        label.textContent = fv.name;
        btn.appendChild(swatch);
        btn.appendChild(label);
        btn.addEventListener('click', () => this._onFrameSelect(fv.name));
        pillsEl.appendChild(btn);
      });
    }

    /* Pick a concrete variant that matches (size, frame). */
    _findVariant(sizeVal, frameVal) {
      const sizeKey  = 'option' + (this.cfg.sizeOptionIndex + 1);
      const frameKey = this.frameOptionIndex >= 0
        ? 'option' + (this.frameOptionIndex + 1)
        : null;
      // Exact match first
      let v = this.cfg.variants.find(x =>
        x[sizeKey] === sizeVal && (!frameKey || x[frameKey] === frameVal)
      );
      if (v) return v;
      // Fall back to size-only match
      return this.cfg.variants.find(x => x[sizeKey] === sizeVal) || null;
    }

    _onSizeSelect(sizeVal) {
      const current = this._currentVariant();
      const frameKey = this.frameOptionIndex >= 0
        ? 'option' + (this.frameOptionIndex + 1) : null;
      const frameVal = (current && frameKey) ? current[frameKey] : null;
      const v = this._findVariant(sizeVal, frameVal);
      if (!v) return;
      this.currentVariantId = v.id;
      this._highlightPills();
      this._highlightFramePills();
      this._syncToMainPage(v);
      this._refreshScene();
    }

    _onFrameSelect(frameVal) {
      const current = this._currentVariant();
      const sizeKey = 'option' + (this.cfg.sizeOptionIndex + 1);
      const sizeVal = current ? current[sizeKey] : null;
      if (!sizeVal) {
        // No size picked yet — remember the frame choice for when one is picked.
        this._pendingFrame = frameVal;
        this._highlightFramePills(frameVal);
        return;
      }
      this._pendingFrame = null;
      const v = this._findVariant(sizeVal, frameVal);
      if (!v) return;
      this.currentVariantId = v.id;
      this._highlightPills();
      this._highlightFramePills();
      this._syncToMainPage(v);
      this._refreshScene();
    }

    _highlightPills() {
      const optKey = 'option' + (this.cfg.sizeOptionIndex + 1);
      const v      = this._currentVariant();
      const active = v ? v[optKey] : null;

      const popularNorm = this.popularSize
        ? this.popularSize.replace(/[×\u201d"]/g,'x').replace(/[^0-9x]/gi,'').toLowerCase()
        : null;

      this.dialog.querySelectorAll('.sg-size-pill').forEach(b => {
        b.classList.toggle('is-active', b.dataset.sizeVal === active);
        if (popularNorm) {
          const vNorm = b.dataset.sizeVal.replace(/[×\u201d"]/g,'x').replace(/[^0-9x]/gi,'').toLowerCase();
          const inchNorm = normSizeKey(b.dataset.sizeVal);
          b.classList.toggle('is-popular', vNorm === popularNorm || inchNorm === popularNorm);
        }
      });
    }

    _highlightFramePills(override) {
      if (this.frameOptionIndex < 0) return;
      const v = this._currentVariant();
      const frameKey = 'option' + (this.frameOptionIndex + 1);
      const active = override || (v ? v[frameKey] : null);
      this.dialog.querySelectorAll('.sg-frame-pill').forEach(b => {
        b.classList.toggle('is-active', b.dataset.frameVal === active);
      });
      const selectedSpan = this.dialog.querySelector('[data-sg-selected-frame]');
      if (selectedSpan) {
        selectedSpan.textContent = active ? ': ' + active : '';
      }
    }

    _activateRoom(roomId) {
      if (!roomId) return;
      this.currentRoomId = roomId;
      this.dialog.querySelectorAll('.sg-room').forEach(r =>
        r.classList.toggle('is-active', r.dataset.room === roomId));
      this.dialog.querySelectorAll('.sg-room-tab').forEach(t =>
        t.classList.toggle('is-active', t.dataset.target === roomId));
      this._refreshScene();
    }

    _refreshScene() {
      const room = this.dialog.querySelector('.sg-room.is-active');
      if (!room) return;

      /* Room tip */
      const roomData = this.cfg.rooms[this.currentRoomId] || {};
      const tipEl = this.dialog.querySelector('[data-sg-tip]');
      if (tipEl) tipEl.textContent = roomData.tipOverride || ROOM_TIPS[this.currentRoomId] || '';

      /* Popular badge — product-derived, same for all rooms */
      const badge    = this.dialog.querySelector('[data-sg-popular]');
      const badgeTxt = this.dialog.querySelector('[data-sg-popular-text]');
      if (badge) {
        badge.classList.toggle('is-visible', !!this.popularSize);
        if (badgeTxt && this.popularSize)
          badgeTxt.textContent = 'Most popular size: ' + this.popularSize;
      }

      /* ── Size-dependent updates ── */
      const v      = this._currentVariant();
      const optKey = 'option' + (this.cfg.sizeOptionIndex + 1);
      const sizeVal = v ? v[optKey] : '';

      /* Dimension display — orientation-aware using artwork image dimensions */
      const dim = formatDimDisplay(sizeVal, this.orientation, this.artW, this.artH);
      const dimP = this.dialog.querySelector('[data-sg-dim-primary]');
      const dimS = this.dialog.querySelector('[data-sg-dim-secondary]');
      if (dimP) dimP.textContent = dim.primary;
      /* Hide the cm secondary label for United States visitors. Inch-equivalents
         for paper sizes (A4 → 8.3×11.7") stay visible since they don't contain " cm". */
      let secondaryText = dim.secondary || '';
      if (this.cfg.hideCmSecondary && /cm/i.test(secondaryText)) {
        secondaryText = '';
      }
      if (dimS) dimS.textContent = secondaryText;

      /* Size note */
      const sizeNoteEl = this.dialog.querySelector('[data-sg-size-note]');
      if (sizeNoteEl) sizeNoteEl.textContent = SIZE_NOTES[normSizeKey(sizeVal)] || '';

      /* ATC button */
      const atcBtn = this.dialog.querySelector('[data-sg-atc]');
      if (atcBtn) {
        let atcLabel = 'Select a size';
        if (sizeVal) {
          const frameKey = this.frameOptionIndex >= 0
            ? 'option' + (this.frameOptionIndex + 1) : null;
          const frameVal = frameKey && v ? v[frameKey] : null;
          atcLabel = frameVal
            ? 'Add ' + sizeVal + ' · ' + frameVal + ' to cart'
            : 'Add ' + sizeVal + ' to cart';
        }
        atcBtn.textContent = atcLabel;
        atcBtn.disabled    = !v || !v.available;
      }

      /* ── Scene (artwork overlay) ── */
      if (!sizeVal) return;
      const size = parseSizeInches(sizeVal, this.orientation, this.artW, this.artH);
      if (!size) return;

      /* Swap in the artwork crop whose aspect ratio matches the selected
         size. Without this a 1×4 size would render the 1×3 primary image
         and object-fit:cover would crop the sides off. */
      const bestArtUrl = this._pickArtworkUrl(size);
      if (bestArtUrl) {
        this.dialog.querySelectorAll('.sg-artwork-img').forEach(img => {
          if (img.getAttribute('src') !== bestArtUrl) {
            img.setAttribute('src', bestArtUrl);
          }
        });
      }

      const wallWidthIn  = parseFloat(room.dataset.wallWidth) || 144;
      const artTopPct    = parseFloat(room.dataset.artTop)    || 26;
      const artLeftPct   = parseFloat(room.dataset.artLeft)   || 50;
      const artMiddlePct = parseFloat(room.dataset.artMiddle) || 45;
      const sceneW       = room.offsetWidth || 800;
      const pxPerInch    = sceneW / wallWidthIn;
      const artWpx = Math.max(30, Math.round(size.w * pxPerInch));
      const artHpx = Math.max(30, Math.round(size.h * pxPerInch));

      /* Special case: tall vertical artwork (1×3 or 1×4 portrait) in Hallway   */
      /* → centre-anchor instead of bottom-anchor for a better hallway preview. */
      const isTallVertical = (this.ratioType === '3x1' || this.ratioType === '1x4')
                             && this.orientation === 'portrait';
      const useCentreAnchor = isTallVertical && this.currentRoomId === 'room5';

      const frame = room.querySelector('.sg-artwork-frame');
      if (frame) {
        /* Paint the frame canvas + inset the artwork. Called first so
           outer width/height can include the frame padding below. */
        const dims = this._applyFramePreview(frame, artWpx, artHpx, pxPerInch);
        frame.style.width  = dims.outerW + 'px';
        frame.style.height = dims.outerH + 'px';
        frame.style.left   = artLeftPct + '%';
        if (useCentreAnchor) {
          frame.style.transform = 'translate(-50%, -50%)';
          frame.style.top       = artMiddlePct + '%';
        } else {
          frame.style.transform = 'translate(-50%, -100%)';
          frame.style.top       = artTopPct + '%';
        }
      }

      const rulerLabel = room.querySelector('.sg-ruler-label');
      if (rulerLabel) {
        let displayW = size.w;
        if (this.frameOptionIndex >= 0 && v) {
          const fv = v['option' + (this.frameOptionIndex + 1)] || '';
          const lowFv = fv.toLowerCase().trim();
          const isUnframed = !lowFv || /stretch|no frame|unframed/.test(lowFv);
          if (!isUnframed) displayW += 2;
        }
        rulerLabel.textContent = displayW + '\u2033 wide';
      }
    }

    _currentVariant() {
      return this.cfg.variants.find(v => v.id === this.currentVariantId) || null;
    }

    /* Paint the selected frame on the room-preview artwork using the real
       frame texture (same assets the AR view + live mockup use). Frame
       thickness and reveal gap are computed from the physical scale
       (pxPerInch) so a 0.5" moulding never reads as a huge slab on a
       48"-wide piece.

       Uses a canvas-based renderer (same logic as the product-page live
       mockup) so textures render as real mitred wood/metal strips with a
       moulding bevel — not stretched CSS backgrounds.

       Returns { outerW, outerH } — the total framed-piece dimensions the
       caller should apply to the frame element's width/height. */
    _applyFramePreview(frameEl, artWpx, artHpx, pxPerInch) {
      if (!frameEl) return { outerW: artWpx, outerH: artHpx };

      const frameImg = frameEl.querySelector('.sg-artwork-img');
      const canvas   = frameEl.querySelector('.sg-frame-canvas');

      // Default: no frame — artwork fills the whole frame element.
      const applyUnframed = () => {
        frameEl.classList.remove('has-frame');
        if (frameImg) {
          frameImg.style.top    = '0';
          frameImg.style.left   = '0';
          frameImg.style.width  = '100%';
          frameImg.style.height = '100%';
          frameImg.style.right  = 'auto';
          frameImg.style.bottom = 'auto';
        }
      };

      if (this.frameOptionIndex < 0) {
        applyUnframed();
        return { outerW: artWpx, outerH: artHpx };
      }
      const v = this._currentVariant();
      const frameKey = 'option' + (this.frameOptionIndex + 1);
      const val = v ? (v[frameKey] || '') : '';
      const low = val.toLowerCase().trim();
      const isUnframed = !low || /stretch|no frame|unframed/.test(low);
      if (isUnframed) {
        applyUnframed();
        return { outerW: artWpx, outerH: artHpx };
      }

      // Look up texture URL from the frame-values JSON (Liquid already
      // geo-picked the right gold texture etc).
      let texUrl = '';
      if (Array.isArray(this.frameValues)) {
        const match = this.frameValues.find(fv =>
          String(fv.name || '').toLowerCase().trim() === low
        );
        if (match && match.texture) texUrl = match.texture;
      }
      if (!texUrl) {
        // No texture available — fall back to the unframed look rather
        // than a solid colour slab which was the old behaviour.
        applyUnframed();
        return { outerW: artWpx, outerH: artHpx };
      }

      // Physical-scale frame profile (matches the live-mockup: 0.5" thick,
      // 0.25" reveal). Both are in inches × pxPerInch so the proportions
      // shrink naturally on bigger pieces the way a real frame does — a
      // 48" canvas has the same 0.25" reveal as a 12" canvas, which reads
      // as a proportionally smaller gap on the larger piece. Minimum
      // floors keep tiny room-scale renders from collapsing to 0.
      const framePx  = Math.max(3, Math.round(0.5  * pxPerInch));
      const revealPx = Math.max(1, Math.round(0.25 * pxPerInch));
      const innerPad = framePx + revealPx;

      const outerW = artWpx + innerPad * 2;
      const outerH = artHpx + innerPad * 2;

      frameEl.classList.add('has-frame');

      // Artwork sits inset by frame + reveal — same as before.
      if (frameImg) {
        frameImg.style.top    = innerPad + 'px';
        frameImg.style.left   = innerPad + 'px';
        frameImg.style.right  = innerPad + 'px';
        frameImg.style.bottom = innerPad + 'px';
        frameImg.style.width  = artWpx + 'px';
        frameImg.style.height = artHpx + 'px';
      }

      // Paint the frame to the canvas. If the texture is still loading,
      // kick off the load and repaint when it's ready — the unframed
      // look is shown in the meantime so there's no broken mid-state.
      if (canvas) {
        const cachedTex = _SG_TEX_CACHE[texUrl];
        sgLoadTexture(texUrl).then((tex) => {
          if (!tex) return;
          // Bail if the user has since switched to a different frame —
          // avoids a late load painting over a newer selection.
          const nowVariant = this._currentVariant();
          const nowVal = nowVariant ? (nowVariant[frameKey] || '').toLowerCase().trim() : '';
          if (nowVal !== low) return;
          sgPaintFrame(canvas, tex, outerW, outerH, framePx, revealPx, low);
        });
        // If the texture was already cached (resolved promise) the .then
        // above runs synchronously-ish on the microtask queue, which is
        // fine — the frame will appear in the same tick as the resize.
        void cachedTex;
      }

      return { outerW, outerH };
    }

    _syncToMainPage(variant) {
      // Update the page's picker so what the customer sees outside the
      // popup reflects their selection. Two-step:
      //   1) flip the matching option radios (drives variant-picker UI)
      //   2) set the hidden variant id as a belt-and-braces fallback
      for (let pos = 1; pos <= 3; pos++) {
        const val = variant['option' + pos];
        if (!val) continue;
        const valLower = String(val).toLowerCase().trim();
        const candidates = document.querySelectorAll(
          'input[type="radio"][data-option-position="' + pos + '"]'
        );
        candidates.forEach(r => {
          let txt = '';
          if (r.labels && r.labels.length > 0) {
            txt = r.labels[0].textContent;
          } else if (r.nextElementSibling) {
            txt = r.nextElementSibling.textContent;
          }
          txt = (txt || '').toLowerCase().trim();
          
          // Clean up multi-line spaces that might come from internal spans
          txt = txt.replace(/\s+/g, ' ');

          // Prioritize the exact data-sg-val we injected on the main picker, 
          // fall back to reading DOM text / IDs as a safety net.
          const valAttr = (r.value || '').toLowerCase().trim();
          const sgVal = (r.getAttribute('data-sg-val') || '').toLowerCase().trim();

          const isMatch = (sgVal === valLower) ||
                          (txt && txt.indexOf(valLower) !== -1) ||
                          (valLower && txt === valLower) ||
                          (valAttr && valAttr.indexOf(valLower) !== -1) ||
                          (valAttr === valLower);

          if (isMatch && !r.checked) {
            r.checked = true;
            r.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      }
      const idInput = document.querySelector('form[action*="/cart/add"] input[name="id"]')
                   || document.querySelector('input[name="id"]');
      if (idInput && idInput.value !== String(variant.id)) {
        idInput.value = variant.id;
        idInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    /* Adds the selected variant straight to the cart via Shopify's Ajax API,
       then fires the same events the theme's ProductForm does — including
       baseEvent:"variant:add" + onSuccessDo:"force_open_drawer" on
       documentElement's cart:change — so the cart drawer auto-opens on
       return to the product page. Falls back to syncing + clicking the
       page's ATC button if anything fails. */
    async _addToCart(variant, btn) {
      const originalLabel = btn.textContent;
      btn.disabled = true;
      btn.textContent = 'Adding...';
      try {
        const rootUrl = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';

        // 1) Ask every registered section to pre-declare what it needs
        //    refreshed. cart-drawer, cart-notification, header-cart etc.
        //    each push their section ids into the array.
        const sectionsToBundle = [];
        document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
          bubbles: true,
          detail: { sections: sectionsToBundle }
        }));

        // 2) POST to /cart/add.js with the sections the theme asked for.
        const formData = new FormData();
        formData.append('id', variant.id);
        formData.append('quantity', '1');
        formData.append('sections', sectionsToBundle.join(','));
        formData.append('sections_url', rootUrl + 'variants/' + variant.id);
        formData.append('on_success', 'force_open_drawer');

        const addRes = await fetch(rootUrl + 'cart/add.js', {
          method: 'POST',
          headers: { 'X-Requested-With': 'XMLHttpRequest' },
          body: formData
        });
        if (!addRes.ok) throw new Error('cart/add.js failed: ' + addRes.status);
        const addJson = await addRes.json();

        // 3) Pull the current cart (so every listener gets item count,
        //    totals, etc.) and stitch the sections in.
        const cartRes = await fetch(rootUrl + 'cart.js');
        const cartContent = await cartRes.json();
        cartContent.sections = addJson.sections;

        // 4) Keep the page's variant picker in sync so the product form
        //    shows the same selection the customer added from the popup.
        this._syncToMainPage(variant);

        // 5) Fire the theme's standard events. cart:change on documentElement
        //    is what the cart-drawer listens to — when baseEvent is
        //    "variant:add" and onSuccessDo is "force_open_drawer", it auto-
        //    opens. variant:add on the product form lets buy-buttons /
        //    notifications react the same way they would on a normal ATC.
        const productForm = document.querySelector('form[action*="/cart/add"]');
        if (productForm) {
          productForm.dispatchEvent(new CustomEvent('variant:add', {
            bubbles: true,
            detail: {
              items: addJson.hasOwnProperty('items') ? addJson.items : [addJson],
              cart: cartContent,
              onSuccessDo: 'force_open_drawer'
            }
          }));
        }
        document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
          bubbles: true,
          detail: {
            baseEvent: 'variant:add',
            onSuccessDo: 'force_open_drawer',
            cart: cartContent
          }
        }));

        btn.textContent = 'Added to cart';
        this.close();
        setTimeout(() => { btn.textContent = originalLabel; btn.disabled = false; }, 1200);
      } catch (err) {
        console.warn('[SizeGuide] cart/add.js failed, falling back:', err);
        this._syncToMainPage(variant);
        this.close();
        // Last-resort fallback: let the page's real ATC button do the work.
        setTimeout(() => {
          const pageBtn = document.querySelector('buy-buttons [type="submit"], [data-product-form] [type="submit"]');
          if (pageBtn && !pageBtn.disabled) pageBtn.click();
        }, 80);
        btn.textContent = originalLabel;
        btn.disabled = false;
      }
    }
  }

  /* ── Init — use event delegation so it survives product-rerender DOM swaps ─ */
  document.addEventListener('click', function (e) {
    const btn = e.target.closest('[data-sg-open]');
    if (!btn) return;
    const id = btn.dataset.sgOpen;
    if (!document.getElementById(id)) return;
    if (!window.__sgInstances) window.__sgInstances = {};
    if (!window.__sgInstances[id]) window.__sgInstances[id] = new SizeGuide(id);
    window.__sgInstances[id].open();
  });

})();
})();
